import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  ArrowRight01Icon, 
  ArrowLeft01Icon,
  HourglassIcon,
  Clock01Icon,
  Money01Icon,
  UserCircleIcon,
  BankIcon,
  SmartPhone01Icon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import { api } from '../utils/api';
import { FadeLoader } from 'react-spinners';
import ReelText from '../components/ui/ReelText';

const RequestTimer = ({ createdAt }) => {
  const [timeLeft, setTimeLeft] = useState(1200); // 20 mins default

  useEffect(() => {
    const created = new Date(createdAt).getTime();
    const expiry = created + (20 * 60 * 1000);
    
    const update = () => {
      const now = new Date().getTime();
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setTimeLeft(diff);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [createdAt]);

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono font-black text-xs ${timeLeft < 300 ? 'bg-red-500/10 text-red-500 animate-pulse' : 'bg-black/5 dark:bg-white/5 text-muted-gray'}`}>
      <span className="text-[8px] uppercase tracking-tighter opacity-50 mr-1 font-sans">Started</span>
      <HugeiconsIcon icon={Clock01Icon} size={12} />
      {mins}:{secs.toString().padStart(2, '0')}
    </div>
  );
};

const RequestListPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('deposit'); // 'deposit' | 'withdrawal'
  const navigate = useNavigate();

  const fetchRequests = async () => {
    try {
      const response = await api.get('/client/transactions');
      const txs = response.data?.transactions || response.data?.data || [];
      
      // Filter for active/pending - Now including 'verified' for ongoing withdrawals
      const active = txs.filter(tx => ['pending', 'pending_verification', 'processing', 'verified'].includes(tx.status));
      setTransactions(active);
    } catch (err) {
      console.warn('Failed to fetch requests:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(fetchRequests, 5000);
    return () => clearInterval(interval);
  }, []);

  const filteredTransactions = transactions.filter(tx => {
    const type = tx.order?.transaction_type || tx.transaction_type;
    return type === activeTab;
  });

  if (loading && transactions.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <FadeLoader color="#4ade80" />
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-20 max-w-5xl mx-auto">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-charcoal dark:text-white tracking-tight">Active Orders</h1>
          <p className="text-muted-gray dark:text-gray-300 mt-2 font-medium">Track and manage your ongoing {activeTab}s in real-time.</p>
        </div>
        
        <div className="w-full md:w-auto bg-off-white dark:bg-gray-800 p-1.5 rounded-2xl flex gap-1 border border-light-gray/20 dark:border-gray-700 shadow-inner">
          <button 
            onClick={() => setActiveTab('deposit')}
            className={`flex-1 md:flex-none px-8 py-3 rounded-xl text-sm font-black transition-all ${activeTab === 'deposit' ? 'bg-white dark:bg-dark-surface shadow-md text-soft-green scale-[1.02]' : 'text-muted-gray hover:text-charcoal dark:hover:text-gray-300'}`}
          >
            Deposits
          </button>
          <button 
            onClick={() => setActiveTab('withdrawal')}
            className={`flex-1 md:flex-none px-8 py-3 rounded-xl text-sm font-black transition-all ${activeTab === 'withdrawal' ? 'bg-white dark:bg-dark-surface shadow-md text-soft-green scale-[1.02]' : 'text-muted-gray hover:text-charcoal dark:hover:text-gray-300'}`}
          >
            Withdrawals
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredTransactions.length === 0 ? (
          <div className="col-span-full bg-white/50 dark:bg-dark-surface/50 backdrop-blur-md p-20 rounded-[3rem] border-2 border-dashed border-light-gray/20 dark:border-dark-border/20 text-center">
            <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-full inline-block mb-4 text-muted-gray opacity-20">
              <HugeiconsIcon icon={HourglassIcon} size={48} />
            </div>
            <h3 className="text-xl font-black text-charcoal dark:text-white">All caught up!</h3>
            <p className="text-muted-gray mt-2">You don&apos;t have any active {activeTab} orders right now.</p>
            <Button onClick={() => navigate('/')} className="mt-8 px-10 py-4 rounded-2xl font-black shadow-lg">Browse Agents</Button>
          </div>
        ) : (
          filteredTransactions.map((tx) => (
            <div 
              key={tx.uuid} 
              className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 shadow-float-sm hover:shadow-float transition-all cursor-pointer group flex flex-col overflow-hidden" 
              onClick={() => navigate(`/transaction/${tx.uuid}`)}
            >
              <div className="p-6 sm:p-8 flex-1">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${activeTab === 'deposit' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'}`}>
                      <HugeiconsIcon icon={activeTab === 'deposit' ? ArrowRight01Icon : ArrowLeft01Icon} size={24} />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-muted-gray uppercase tracking-widest mb-0.5">Order Reference</p>
                      <p className="font-bold text-charcoal dark:text-white font-mono text-sm truncate max-w-[120px]">{tx.reference.split('-').pop()}</p>
                    </div>
                  </div>
                  <RequestTimer createdAt={tx.created_at} />
                </div>

                <div className="space-y-6">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-[10px] font-black text-muted-gray uppercase tracking-widest mb-1">Total Amount</p>
                      <p className="text-3xl font-black text-soft-green flex items-center gap-1">
                        <span>₦</span>
                        <ReelText text={parseFloat(tx.amount_sent).toLocaleString()} />
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        ['completed', 'verified'].includes(tx.status) ? 'bg-soft-green/10 text-soft-green' : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                      }`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${['completed', 'verified'].includes(tx.status) ? 'bg-soft-green' : 'bg-yellow-500 animate-pulse'}`}></div>
                        {tx.status.replace('_', ' ')}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-100 dark:border-white/5">
                    <div className="flex items-center gap-2">
                      <HugeiconsIcon icon={UserCircleIcon} size={14} className="text-muted-gray" />
                      <div className="min-w-0">
                        <p className="text-[8px] font-black text-muted-gray uppercase tracking-tighter leading-none mb-1">Authorized Agent</p>
                        <p className="text-xs font-bold text-charcoal dark:text-white truncate">{tx.merchant?.name || tx.order?.merchant?.name || 'JeanCryptoPay'}</p>
                      </div>
                    </div>
                    {activeTab === 'deposit' && (
                      <div className="flex items-center gap-2">
                        <HugeiconsIcon icon={Money01Icon} size={14} className="text-muted-gray" />
                        <div>
                          <p className="text-[8px] font-black text-muted-gray uppercase tracking-tighter leading-none mb-1">Profit Share</p>
                          <p className="text-xs font-bold text-purple-500">{(parseFloat(tx.order?.cashback_rate || 0) * 100).toFixed(0)}% Reward</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-8 py-4 bg-gray-50 dark:bg-black/20 flex items-center justify-between group-hover:bg-soft-green/5 transition-colors">
                <span className="text-[10px] font-black text-muted-gray uppercase tracking-widest group-hover:text-soft-green transition-colors">View Details</span>
                <HugeiconsIcon icon={ArrowRight01Icon} size={16} className="text-muted-gray group-hover:text-soft-green group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RequestListPage;
